import React, { useCallback, useEffect, useRef, useState } from "react";

import { Ingredient, ingredientsApi } from "@/client/api";
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorMessage,
  FormField,
  Input,
  Link,
  Modal,
  PageHeader,
  Select,
  Spinner,
  TopLoadingBar,
} from "@/client/components/ui";
import { MasterDetailLayout } from "@/client/components/ui/MasterDetailLayout";
import {
  INGREDIENT_TYPE_EMOJIS as TYPE_EMOJIS,
  INGREDIENT_TYPE_LABELS as TYPE_LABELS,
  INGREDIENT_TYPE_ORDER as TYPE_ORDER,
} from "@/client/constants";
import { useToast } from "@/client/context/ToastContext";
import { useApi } from "@/client/hooks/useApi";
import { useKey } from "@/client/hooks/useKey";
import { AddIcon, DeleteIcon, EditIcon, IngredientIcon } from "@/client/icons";
import { normalize } from "@/client/utils";
import { formatQty, formatQtyStr } from "@/client/utils/formatting";
import { UNIT_OPTION_GROUPS } from "@/shared/units";

const TYPE_SELECT_OPTIONS = TYPE_ORDER.map((t) => ({
  value: t,
  label: `${TYPE_EMOJIS[t]} ${TYPE_LABELS[t]}`,
}));

function normalizeIngredientType(type: string | null | undefined): string {
  if (type && TYPE_ORDER.includes(type)) return type;
  return "Other";
}

// ─── New ingredient modal ─────────────────────────────────────────────────────

function NewIngredientModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [type, setType] = useState("Other");
  const [saving, setSaving] = useState(false);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      toast("Escribí un nombre", "error");
      return;
    }
    setSaving(true);
    try {
      const created = await ingredientsApi.create({ name: trimmed, type });
      toast("Ingrediente creado");
      onCreated(created.id);
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Nuevo ingrediente"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={saving}>
            {saving ? "Guardando..." : "Crear"}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        <FormField label="Nombre">
          <Input value={name} onChange={setName} placeholder="Ej. Morrón rojo" autoFocus />
        </FormField>
        <FormField label="Categoría">
          <Select value={type} onChange={setType} options={TYPE_SELECT_OPTIONS} />
        </FormField>
      </form>
    </Modal>
  );
}

// ─── Conversions section ─────────────────────────────────────────────────────

const COUNTABLE_UNITS = UNIT_OPTION_GROUPS.find((g) => g.label === "Contable")!.options.map(
  (o) => o.value,
);

function ConversionsSection({ ingredientId }: { ingredientId: string }) {
  const { toast } = useToast();
  const fetcher = useCallback(() => ingredientsApi.conversions(ingredientId), [ingredientId]);
  const { data: conversions, loading, refetch } = useApi(fetcher, [ingredientId]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<
    { from_unit: string; to_unit: string; factor_min: string; factor_max: string }[]
  >([]);
  const [saving, setSaving] = useState(false);

  function startEdit() {
    setDraft(
      (conversions ?? []).map((c) => ({
        from_unit: c.from_unit,
        to_unit: c.to_unit,
        factor_min: String(c.factor_min),
        factor_max: String(c.factor_max),
      })),
    );
    setEditing(true);
  }

  function addRow() {
    const usedUnits = new Set(draft.map((d) => d.from_unit));
    const next = COUNTABLE_UNITS.find((u) => !usedUnits.has(u)) ?? COUNTABLE_UNITS[0]!;
    setDraft([...draft, { from_unit: next, to_unit: "g", factor_min: "", factor_max: "" }]);
  }

  async function save() {
    const parsed = draft
      .filter((d) => d.factor_min.trim())
      .map((d) => ({
        from_unit: d.from_unit,
        to_unit: d.to_unit,
        factor_min: parseFloat(d.factor_min),
        factor_max: d.factor_max.trim() ? parseFloat(d.factor_max) : parseFloat(d.factor_min),
      }));
    for (const c of parsed) {
      if (isNaN(c.factor_min) || c.factor_min <= 0) {
        toast("El valor debe ser un número positivo", "error");
        return;
      }
    }
    setSaving(true);
    try {
      await ingredientsApi.updateConversions(ingredientId, parsed);
      toast("Conversiones guardadas");
      setEditing(false);
      void refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-medium text-term-text-dim uppercase tracking-wide">
          Conversiones
        </h3>
        {!editing && !loading && (
          <button
            onClick={startEdit}
            className="text-micro text-term-text-muted hover:text-term-accent cursor-pointer transition-colors"
          >
            {conversions?.length ? "Editar" : "+ Agregar"}
          </button>
        )}
      </div>

      {loading && <Spinner />}

      {!loading && !editing && conversions && conversions.length === 0 && (
        <p className="text-sm text-term-text-muted">Sin conversiones definidas.</p>
      )}

      {!loading && !editing && conversions && conversions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {conversions.map((c) => (
            <span
              key={c.from_unit}
              className="text-sm bg-term-raised px-2 py-1 rounded text-term-text-dim"
            >
              1 {c.from_unit} ≈ {c.factor_min}
              {c.factor_max !== c.factor_min ? `–${c.factor_max}` : ""} {c.to_unit}
            </span>
          ))}
        </div>
      )}

      {editing && (
        <div className="flex flex-col gap-2">
          {draft.map((row, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="text-term-text-muted text-sm w-4 shrink-0">1</span>
              <select
                value={row.from_unit}
                onChange={(e) => {
                  const updated = [...draft];
                  updated[i] = { ...updated[i]!, from_unit: e.target.value };
                  setDraft(updated);
                }}
                className="w-24 px-2 py-1.5 text-sm bg-term-bg border border-term-border rounded text-term-text"
              >
                {COUNTABLE_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
              <span className="text-term-text-muted text-sm">≈</span>
              <Input
                value={row.factor_min}
                onChange={(v) => {
                  const updated = [...draft];
                  updated[i] = { ...updated[i]!, factor_min: v };
                  setDraft(updated);
                }}
                type="number"
                placeholder="mín"
                className="w-16"
              />
              <span className="text-term-text-muted text-sm">–</span>
              <Input
                value={row.factor_max}
                onChange={(v) => {
                  const updated = [...draft];
                  updated[i] = { ...updated[i]!, factor_max: v };
                  setDraft(updated);
                }}
                type="number"
                placeholder="máx"
                className="w-16"
              />
              <select
                value={row.to_unit}
                onChange={(e) => {
                  const updated = [...draft];
                  updated[i] = { ...updated[i]!, to_unit: e.target.value };
                  setDraft(updated);
                }}
                className="w-14 px-1 py-1.5 text-sm bg-term-bg border border-term-border rounded text-term-text"
              >
                <option value="g">g</option>
                <option value="ml">ml</option>
              </select>
              <button
                onClick={() => setDraft(draft.filter((_, j) => j !== i))}
                className="text-term-text-muted hover:text-term-error cursor-pointer"
              >
                <DeleteIcon width={12} height={12} />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-2 mt-1">
            <button
              onClick={addRow}
              className="text-sm text-term-accent hover:bg-term-accent/10 cursor-pointer px-2 py-1 rounded-md transition-colors"
            >
              + agregar
            </button>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" onClick={() => void save()} disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ingredient detail panel ──────────────────────────────────────────────────

function IngredientDetailPanel({
  ingredient,
  onSaved,
  onDeleted,
}: {
  ingredient: Ingredient;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(ingredient.name);
  const [type, setType] = useState(normalizeIngredientType(ingredient.type));
  const [staple, setStaple] = useState(ingredient.staple === 1);
  const [density, setDensity] = useState(ingredient.density_g_per_ml?.toString() ?? "");
  const [preferredUnit, setPreferredUnit] = useState(ingredient.preferred_unit ?? "");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Reset when ingredient changes
  useEffect(() => {
    setName(ingredient.name);
    setType(normalizeIngredientType(ingredient.type));
    setStaple(ingredient.staple === 1);
    setDensity(ingredient.density_g_per_ml?.toString() ?? "");
    setPreferredUnit(ingredient.preferred_unit ?? "");
    setEditing(false);
    setConfirmDelete(false);
  }, [
    ingredient.id,
    ingredient.name,
    ingredient.type,
    ingredient.staple,
    ingredient.density_g_per_ml,
    ingredient.preferred_unit,
  ]);

  async function confirmRemove() {
    try {
      await ingredientsApi.delete(ingredient.id);
      toast("Ingrediente eliminado");
      setConfirmDelete(false);
      onDeleted();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    }
  }

  const parsedDensity = density.trim() ? parseFloat(density) : null;
  const densityChanged = (parsedDensity ?? null) !== (ingredient.density_g_per_ml ?? null);
  const parsedPreferredUnit = preferredUnit || null;
  const preferredUnitChanged = parsedPreferredUnit !== (ingredient.preferred_unit ?? null);

  const dirty =
    name.trim() !== ingredient.name ||
    normalizeIngredientType(ingredient.type) !== type ||
    staple !== (ingredient.staple === 1) ||
    densityChanged ||
    preferredUnitChanged;

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) {
      toast("El nombre no puede estar vacío", "error");
      return;
    }
    if (parsedDensity != null && (isNaN(parsedDensity) || parsedDensity <= 0)) {
      toast("La densidad debe ser un número positivo", "error");
      return;
    }
    if (!dirty) return;
    setSaving(true);
    try {
      await ingredientsApi.update(ingredient.id, {
        name: trimmed,
        type,
        staple,
        density_g_per_ml: parsedDensity,
        preferred_unit: parsedPreferredUnit,
      });
      toast("Cambios guardados");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setName(ingredient.name);
    setType(normalizeIngredientType(ingredient.type));
    setStaple(ingredient.staple === 1);
    setDensity(ingredient.density_g_per_ml?.toString() ?? "");
    setPreferredUnit(ingredient.preferred_unit ?? "");
    setEditing(false);
  }

  const emoji = TYPE_EMOJIS[normalizeIngredientType(ingredient.type)] ?? "📦";
  const typeLabel = TYPE_LABELS[normalizeIngredientType(ingredient.type)] ?? ingredient.type;

  // Recipes panel
  const fetcher = useCallback(() => ingredientsApi.recipes(ingredient.id), [ingredient.id]);
  const {
    data: recipes,
    loading: recipesLoading,
    error: recipesError,
  } = useApi(fetcher, [ingredient.id]);

  return (
    <div className="flex flex-col gap-5">
      {/* Header — read-only or edit */}
      {editing ? (
        <Card className="p-4 flex flex-col gap-3">
          <FormField label="Nombre">
            <Input value={name} onChange={setName} placeholder="Nombre del ingrediente" />
          </FormField>
          <FormField label="Categoría">
            <Select value={type} onChange={setType} options={TYPE_SELECT_OPTIONS} />
          </FormField>
          <FormField label="Densidad (g/ml)" hint="Unifica g y ml en la lista de compras">
            <Input
              value={density}
              onChange={setDensity}
              type="number"
              placeholder="Ej. 0.92 (aceite)"
            />
          </FormField>
          <FormField label="Unidad preferida" hint="Unidad principal en la lista de compras">
            <Select
              value={preferredUnit}
              onChange={setPreferredUnit}
              options={[
                { value: "", label: "Sin preferencia" },
                ...UNIT_OPTION_GROUPS.filter((g) => g.label !== "Descriptivo").flatMap(
                  (g) => g.options,
                ),
              ]}
            />
          </FormField>
          <label className="flex items-center gap-2 text-sm text-term-text-dim cursor-pointer">
            <input
              type="checkbox"
              checked={staple}
              onChange={(e) => setStaple(e.target.checked)}
              className="accent-term-accent"
            />
            Básico de cocina
            <span className="text-micro text-term-text-muted">
              (no aparece en listas de compras)
            </span>
          </label>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => void save()}
              disabled={saving || !dirty}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button variant="ghost" size="sm" onClick={cancel}>
              Cancelar
            </Button>
          </div>
        </Card>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-2xl">{emoji}</span>
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-lg text-term-text">{ingredient.name}</h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-term-text-muted">{typeLabel}</span>
              {ingredient.staple === 1 && (
                <span className="text-micro text-term-text-muted bg-term-raised px-1.5 py-0.5 rounded">
                  Básico
                </span>
              )}
              {ingredient.density_g_per_ml != null && (
                <span className="text-micro text-term-text-muted bg-term-raised px-1.5 py-0.5 rounded">
                  {ingredient.density_g_per_ml} g/ml
                </span>
              )}
              {ingredient.preferred_unit != null && (
                <span className="text-micro text-term-text-muted bg-term-raised px-1.5 py-0.5 rounded">
                  Pref: {ingredient.preferred_unit}
                </span>
              )}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <EditIcon width={13} height={13} />
            Editar
          </Button>
        </div>
      )}

      {/* Recipes list */}
      <div>
        <h3 className="text-xs font-medium text-term-text-dim uppercase tracking-wide mb-2">
          En recetas
        </h3>
        {recipesLoading && <Spinner />}
        {recipesError && <ErrorMessage message={recipesError} />}
        {recipes && recipes.length === 0 && (
          <div className="flex flex-col gap-3 py-2">
            <p className="text-sm text-term-text-muted">
              Ninguna receta usa {ingredient.name} todavía.
            </p>
            <Button
              variant="danger"
              size="sm"
              className="self-start"
              onClick={() => setConfirmDelete(true)}
            >
              <DeleteIcon width={13} height={13} />
              Borrar ingrediente
            </Button>
          </div>
        )}
        {recipes && recipes.length > 0 && (
          <div className="flex flex-col gap-2">
            {recipes.map((r, i) => (
              <Link key={`${r.id}-${r.phase}`} href={`/recetas/${r.id}`}>
                <Card index={i} hoverable className="p-3.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-term-text">{r.name}</span>
                    {r.type && <Badge value={r.type} />}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-term-text-muted">
                    {r.quantity && (
                      <span className="text-term-accent font-medium">
                        {r.unit
                          ? formatQty(parseFloat(r.quantity), r.unit)
                          : formatQtyStr(r.quantity)}
                      </span>
                    )}
                    {r.phase && <span>{r.phase === "meal-prep" ? "Meal prep" : "Día de"}</span>}
                    {r.servings != null && <span>{r.servings} porc.</span>}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Unit conversions */}
      <ConversionsSection ingredientId={ingredient.id} />

      {confirmDelete && (
        <ConfirmModal
          message={`¿Borrar «${ingredient.name}»? Esta acción no se puede deshacer.`}
          confirmLabel="Borrar"
          danger
          onConfirm={() => void confirmRemove()}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function IngredientsPage({ initialIngredientId }: { initialIngredientId?: string } = {}) {
  const [selectedId, setSelectedId] = useState<string | null>(initialIngredientId ?? null);
  const [search, setSearch] = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useKey(
    "/",
    (e) => {
      e.preventDefault();
      searchRef.current?.focus();
    },
    { ignoreInputs: true },
  );

  const fetcher = useCallback(() => ingredientsApi.list(), []);
  const { data: ingredients, loading, error, refetch } = useApi(fetcher, []);

  function selectIngredient(id: string | null) {
    setSelectedId(id);
    const url = id ? `/ingredientes/${id}` : "/ingredientes";
    window.history.replaceState(null, "", url);
  }

  if (loading) return <TopLoadingBar visible />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!ingredients) return null;

  const filtered = ingredients.filter(
    (i) => !search || normalize(i.name).includes(normalize(search)),
  );

  const byType = new Map<string, Ingredient[]>();
  for (const ing of filtered) {
    const t = normalizeIngredientType(ing.type);
    const list = byType.get(t) ?? [];
    list.push(ing);
    byType.set(t, list);
  }
  // Flat ordered list for keyboard navigation
  const flatList = TYPE_ORDER.flatMap((t) => byType.get(t) ?? []);

  const selected = selectedId ? ingredients.find((i) => i.id === selectedId) : null;

  function handleListKeyDown(e: React.KeyboardEvent) {
    if (flatList.length === 0) return;
    const curIdx = flatList.findIndex((i) => i.id === selectedId);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = curIdx < flatList.length - 1 ? curIdx + 1 : 0;
      selectIngredient(flatList[next]!.id);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = curIdx > 0 ? curIdx - 1 : flatList.length - 1;
      selectIngredient(flatList[next]!.id);
    } else if (e.key === "Enter" && selectedId) {
      e.preventDefault();
      // Already selected — detail panel is showing
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto flex flex-col gap-6">
      <PageHeader
        title="Ingredientes"
        icon={<IngredientIcon width={22} height={22} />}
        subtitle={`${ingredients.length} ingrediente${ingredients.length !== 1 ? "s" : ""}`}
        actions={
          <Button variant="primary" size="sm" onClick={() => setShowNewModal(true)}>
            <AddIcon width={14} height={14} />
            Nuevo ingrediente
          </Button>
        }
      />

      {showNewModal && (
        <NewIngredientModal
          onClose={() => setShowNewModal(false)}
          onCreated={(id) => {
            void refetch();
            selectIngredient(id);
          }}
        />
      )}

      {ingredients.length === 0 ? (
        <EmptyState
          icon={<IngredientIcon strokeWidth={1.25} />}
          title="Sin ingredientes"
          description="Creá ingredientes acá o al editar los pasos de una receta."
          action={
            <Button variant="primary" size="sm" onClick={() => setShowNewModal(true)}>
              <AddIcon width={14} height={14} />
              Nuevo ingrediente
            </Button>
          }
        />
      ) : (
        <MasterDetailLayout
          hasSelection={!!selected}
          onBack={() => selectIngredient(null)}
          backLabel="Ingredientes"
          emptyMessage="Seleccioná un ingrediente para ver en qué recetas se usa"
          list={
            <div className="flex flex-col gap-4">
              <Input
                value={search}
                onChange={setSearch}
                type="search"
                placeholder="Buscar ingrediente..."
                autoFocus
                inputRef={searchRef}
                onKeyDown={handleListKeyDown}
              />
              <div className="flex flex-col gap-4 overflow-y-auto max-h-[calc(100dvh-220px)]">
                {TYPE_ORDER.filter((t) => byType.has(t)).map((type, i) => (
                  <div
                    key={type}
                    className="animate-[fade-in-up_0.3s_ease_both]"
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5 px-1">
                      <span className="text-sm">{TYPE_EMOJIS[type]}</span>
                      <span className="text-xs font-medium text-term-text-muted uppercase tracking-wide">
                        {TYPE_LABELS[type]}
                      </span>
                      <span className="text-micro text-term-text-muted">
                        ({byType.get(type)!.length})
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      {byType.get(type)!.map((ing) => (
                        <button
                          key={ing.id}
                          ref={
                            ing.id === selectedId
                              ? (el) => el?.scrollIntoView({ block: "nearest" })
                              : undefined
                          }
                          onClick={() => selectIngredient(ing.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm cursor-pointer transition-all ${
                            selectedId === ing.id
                              ? "border-l-2 border-l-term-accent border-term-accent/30 bg-term-accent/5 text-term-accent font-medium"
                              : "text-term-text-dim hover:bg-term-raised hover:text-term-text"
                          }`}
                        >
                          {ing.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && (
                  <p className="text-sm text-term-text-muted text-center py-4">Sin resultados</p>
                )}
              </div>
            </div>
          }
          detail={
            selected ? (
              <IngredientDetailPanel
                key={selected.id}
                ingredient={selected}
                onSaved={() => void refetch()}
                onDeleted={() => {
                  void refetch();
                  selectIngredient(null);
                }}
              />
            ) : null
          }
        />
      )}
    </div>
  );
}
